import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

import {
  UserAccountEntryState,
  WithdrawalStatus,
} from '#src/__generated__/globalTypes.ts';
import { isTypes } from '#src/lib/gql/index.ts';

import { DumbAccountEntryState } from './DumbAccountEntryState/index.tsx';
import type { AccountEntryState_userAccountEntry } from './__generated__/index.graphql.ts';

type Props = {
  userAccountEntry: AccountEntryState_userAccountEntry;
};

const withdrawalStatusMessages = defineMessages({
  [WithdrawalStatus.CREATED]: {
    defaultMessage: 'Pending email confirmation',
  },
  [WithdrawalStatus.CONFIRMED]: {
    defaultMessage: 'Pending',
  },
  [WithdrawalStatus.SETTLEMENT_PUBLISHED]: {
    defaultMessage: 'Pending',
  },
  [WithdrawalStatus.CANCELLED]: {
    defaultMessage: 'Canceled',
  },
  [WithdrawalStatus.SETTLEMENT_FAILED]: {
    defaultMessage: 'Error',
  },
  [WithdrawalStatus.SETTLED]: {
    defaultMessage: 'Settled',
  },
});

const withdrawalStateMessages = defineMessages({
  [UserAccountEntryState.PENDING]: {
    defaultMessage: 'Pending',
  },
  [UserAccountEntryState.CONFIRMED]: {
    defaultMessage: 'Confirmed',
  },
  [UserAccountEntryState.CANCELLED]: {
    defaultMessage: 'Canceled',
  },
});

export const AccountEntryState = ({ userAccountEntry }: Props) => {
  const { aasmState, tokenOperation } = userAccountEntry;

  if (
    ![
      UserAccountEntryState.PENDING,
      UserAccountEntryState.CANCELLED,
    ].includesValue(userAccountEntry.aasmState)
  )
    return null;

  const isWithDrawal = isTypes(tokenOperation, [
    'Withdrawal',
    'FastWithdrawal',
  ]);

  let label: MessageDescriptor | null = null;
  if (aasmState === UserAccountEntryState.PENDING && isWithDrawal) {
    label = withdrawalStatusMessages[tokenOperation.status];
  } else {
    label = withdrawalStateMessages[aasmState];
  }

  if (!label) return null;

  return <DumbAccountEntryState state={aasmState} label={label} />;
};

AccountEntryState.fragments = {
  userAccountEntry: gql`
    fragment AccountEntryState_userAccountEntry on UserAccountEntry {
      id
      aasmState
      tokenOperation {
        ... on Node {
          id
        }
        ... on FastWithdrawal {
          id
          status
        }
        ... on Withdrawal {
          id
          status
        }
      }
    }
  ` as TypedDocumentNode<AccountEntryState_userAccountEntry>,
};
